<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">{{ item?.id ? $t('locationEditModalTitle') : $t('locationCreateModalTitle') }}</div>
    </template>

    <Spinner v-show="dataIsSending"/>

    <div v-show="!dataIsSending" class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12" :class="{'p-mb-5':selectedCompany}">
        <div class="p-float-label">
          <CustomDropdown id="location-modal-company"
                          data-key="id"
                          @change="changeCompany"
                          :loading="companiesDataLoading"
                          :class="[
                            { 'p-invalid': submitted && !selectedCompany },
                          ]"
                          v-model="selectedCompany"
                          :clearSearchData="!visible"
                          :options="companies"
                          :filter="true"
                          :filterFields="['name', 'reg_number']"
                          :showClear="!item?.id"
                          optionLabel="name">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <span>{{ slotProps.value.name }} <span v-if="slotProps.value.reg_number"> ({{slotProps.value.reg_number}})</span></span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>

            <template #option="slotProps">
              <div>
                <div>{{ slotProps.option.name }} <span v-if="slotProps.option.reg_number"> ({{slotProps.option.reg_number}})</span></div>
              </div>
            </template>
          </CustomDropdown>
          <label for="location-modal-company">{{ $t('Company') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !selectedCompany">{{ $t('Required field') }}</small>
      </div>

      <div v-if="!itemData.id && selectedCompany" class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="location-modal-plan"
                          v-model="selectedPlan"
                          data-key="code"
                          optionLabel="key"
                          :options="plans"
                          :loading="plansDataLoading"
                          :preventFilter="true"
                          :filter="false"
                          :showClear="false"
                          :class="{'p-invalid': submitted && !selectedPlan}">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <strong>{{ slotProps.value.key }}</strong>

                <span class="p-ml-2">
                  {{ formatBillingCurrency(slotProps.value.price, 'EUR', 0, 2) }}
                  {{ $t('billing.planChangeModal.pricePeriodShort') }}
                </span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>

            <template #option="slotProps">
              <div class="p-d-flex p-jc-between p-ai-center">
                <strong>{{ slotProps.option.key }}</strong>

                <span class="p-ml-3">
                  {{ formatBillingCurrency(slotProps.option.price, 'EUR', 0, 2) }}
                  {{ $t('billing.planChangeModal.pricePeriodShort') }}
                </span>
              </div>
            </template>
          </CustomDropdown>

          <label for="location-modal-plan">
            {{ $t('billing.subscriptions.newLocationPlanLabel') }}
            <span class="warning-color">*</span>
          </label>
        </div>

        <small v-if="submitted && !selectedPlan" class="p-invalid">
          {{ $t('Required field') }}
        </small>

<!--        DONT DELETE-->
<!--        <div class="p-text-secondary p-mb-2">-->
<!--          <small>-->
<!--            {{ $t('billing.subscriptions.newLocationPlanHint') }}-->
<!--          </small>-->
<!--        </div>-->
      </div>

      <template v-if="selectedCompany">
        <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
          <div class="p-float-label">
            <InputText id="location-modal-public-name"
                       :disabled="!selectedCompany"
                       :class="{'p-invalid' : submitted && !itemData.public_name}"
                       v-model.trim="itemData.public_name"
                       autocomplete="off"/>
            <label for="location-modal-public-name">{{ $t('locationPublicNameLabel') }}<span class="warning-color">*</span></label>
          </div>
          <small class="p-invalid" v-if="submitted && !itemData.public_name">{{ $t('Required field') }}</small>
          <div class="p-text-secondary p-mb-2">
            <small> {{ $t('locationPublicNameHint') }}</small>
          </div>

          <!--            <div class="p-d-flex p-ai-center p-mt-1">-->
          <!--&lt;!&ndash;              <i class="ti-info-alt p-mr-1"></i>&ndash;&gt;-->
          <!--              <div>{{ $t('locationPublicNameHint') }}</div>-->
          <!--            </div>-->

        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
          <EmailInput v-model="itemData.email"
                      :disabled="!selectedCompany"
                      v-model:isInvalid="emailInvalid"
                      :submitted="submitted"
                      :required="true"
                      inputId="location-modal-email"/>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
          <PhoneInput v-model:phoneCountry="itemData.phone_country"
                      v-model:phoneCode="itemData.phone_code"
                      v-model:phoneNumber="itemData.phone_number"
                      v-model:isInvalid="phoneInvalid"
                      :name="'location-modal'"
                      :required="true"
                      :disabled="!selectedCompany"
                      :modalData="itemData"
                      :visible="visible"
                      :submitted="submitted"/>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
          <div class="p-float-label">
            <InputText id="location-modal-website"
                       :disabled="!selectedCompany"
                       v-model.trim="itemData.website"
                       :class="{'p-invalid': submitted && !websiteLooksValid(itemData.website)}"
                       autocomplete="url"/>
            <label for="location-modal-website">{{ $t('Website') }}</label>
          </div>

          <small v-if="submitted && !websiteLooksValid(itemData.website)" class="p-invalid">
            {{ $t('Invalid website') }}
          </small>
        </div>


        <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
          <AccountAddressFieldset v-model="itemData"
                                  label="locationAddressLabel"
                                  :invalid="submitted && addressIsInvalid"
                                  :modalLayer="1"/>
        </div>
      </template>
    </div>
    <template #footer>
      <Button :label="$t('Cancel')" icon="pi pi-times" class="p-button-text" @click="close"/>
      <Button :label="$t('Save')" :disabled="dataIsSending ||
                plansDataLoading ||
                !selectedCompany ||
                (!itemData.id && !selectedPlan)"
              icon="pi pi-check" class="p-button-text" @click="saveItem"/>
    </template>
  </Modal>
</template>

<script>
import httpClient from '@/services/http.services'
import formatMixins from "@/mixins/formatMixins";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import EmailInput from "@/components/EmailInput";
import PhoneInput from "@/components/PhoneInput";
import AccountAddressFieldset from "@/pages/account/components/AccountAddressFieldset.vue";

export default {
  components: { EmailInput, PhoneInput, AccountAddressFieldset },
  mixins: [ showErrorsMixins, formatMixins, ],
  emits: ['close', 'update-items'],
  name: 'LocationModal',
  props: {
    item: Object,
    visible: Boolean,
  },
  data() {
    return {
      selectedCompany: null,
      companiesDataLoading: false,
      companies: [],
      selectedPlan: null,
      plansDataLoading: false,
      plans: [],
      emailInvalid: false,
      phoneInvalid: false,
      dataIsSending: false,
      itemData: {},
      submitted: false,
    }
  },
  watch: {
    item: {
      immediate: true,
      handler(value) {
        this.setItemData(value)
      },
    },

    visible(value) {
      if (!value) {
        this.dataIsSending = false
        this.submitted = false
        this.selectedCompany = null
        this.selectedPlan = null
        this.itemData = {}
        this.emailInvalid = false
        this.phoneInvalid = false
        return
      }

      this.setItemData(this.item)
      this.getCompanies()

      if (!this.item?.id) {
        this.getPlans()
      }
    },
  },
  methods: {
    setItemData(item) {
      this.itemData = item ? { ...item } : {}
      this.selectedCompany = item?.company ?? null
    },

    selectCompany() {
      const companyId = this.itemData?.tenant_company_id
      if (!companyId) return

      const foundCompany = this.companies.find(c => +c.id === +companyId)

      if (foundCompany) {
        this.selectedCompany = foundCompany
      }
    },
    changeCompany() {
      const company = this.selectedCompany

      if (!company || this.itemData.id) {
        return
      }

      this.itemData = {
        ...this.itemData,

        email: company.privacy_email ?? null,

        phone_country: company.privacy_phone_country ?? null,
        phone_code: company.privacy_phone_code ?? null,
        phone_number: company.privacy_phone_number ?? null,

        address1: company.address1 ?? null,
        address2: company.address2 ?? null,
        postcode: company.postcode ?? null,
        region: company.region ?? null,
        city: company.city ?? null,
        country_id: company.country_id ?? null,
        country: company.country ?? null,
      }
    },
    async getCompanies() {
      if (this.companiesDataLoading || !this.$store.getters.userCanGetData) return
      this.companiesDataLoading = true

      try {
        const { data, status } = await httpClient('tenant-company/modal-companies')
        if (status === 200 && Array.isArray(data)) {
          this.companies = data
        } else {
          this.companies = []
          this.showUnknownError?.()
        }
      } catch (err) {
        this.showError?.(err)
      } finally {
        this.companiesDataLoading = false
        this.selectCompany()
      }
    },
    async getPlans() {
      if (this.plansDataLoading) return

      this.plansDataLoading = true

      try {
        const {data, status} = await httpClient('billing/plans')

        if (status === 200 && data?.success && Array.isArray(data.items)) {
          this.plans = data.items
        } else {
          this.plans = []
          this.showUnknownError()
        }
      } catch (err) {
        this.plans = []
        this.showError(err)
      } finally {
        this.plansDataLoading = false
      }
    },
    async saveItem() {
      this.submitted = true

      if (!this.itemData
          || !this.selectedCompany
          || (!this.itemData.id && !this.selectedPlan)
          || !this.itemData.public_name
          || this.emailInvalid
          || this.phoneInvalid
          || this.addressIsInvalid
          || !this.websiteLooksValid(this.itemData.website)
      ) {
        return false
      }

      this.dataIsSending = true

      const savePhoneNumber = this.itemData.phone_code && this.itemData.phone_number

      const locationData = {
        id: this.itemData.id ?? null,
        public_name: this.itemData.public_name ?? null,
        tenant_company_id: this.selectedCompany ? this.selectedCompany.id : null,
        email: this.itemData.email ?? null,
        phone_country: savePhoneNumber && this.itemData.phone_country ? this.itemData.phone_country : null,
        phone_code: savePhoneNumber ? this.itemData.phone_code : null,
        phone_number: savePhoneNumber ? this.itemData.phone_number : null,
        website: this.normalizeUrl(this.itemData.website) || null,
        address1: this.itemData.address1 ?? null,
        address2: this.itemData.address2 ?? null,
        city: this.itemData.city ?? null,
        region: this.itemData.region ?? null,
        postcode: this.itemData.postcode ?? null,
        country_id: this.itemData.country_id ?? null,
      }

      if (!this.itemData.id) {
        locationData.plan_code = this.selectedPlan.code
      }

      if (this.itemData.id) {
        try {
          const { data, status } = await httpClient.post(`location/update`, locationData)
          if (status === 200 && data?.success) {
            this.$emit('update-items', this.itemData.id)
            this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife});
            this.close()
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.dataIsSending = false
        }
      } else {
        try {
          const { status, data } = await httpClient.post(`location/create`, locationData)
          if (status === 201 && data?.success) {
            this.itemData.id = data.id
            locationData.id = data.id

            this.$emit('update-items', this.itemData.id)

            this.$toast.add({ severity: 'success', detail: this.$t('Data saved'), life: settings.toastLife })
            if (data.billing_warning) {
              this.$toast.add({ severity: 'warn', detail: data.billing_warning, life: settings.toastLife })
            }

            const invoiceId = Number(data.invoice_id || 0)

            this.close()

            if (invoiceId > 0) {
              await this.$router.push({ name: 'BillingInvoices', query: { page: 1, sort: '-id', highlight: invoiceId } })
            }
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.dataIsSending = false
        }
      }
    },
    close() {
      this.$emit('close')
      this.submitted = false
    },
  },
  computed: {
    addressIsInvalid() {
      return !(
          this.itemData?.address1 &&
          this.itemData?.city &&
          this.itemData?.postcode &&
          this.itemData?.country_id
      )
    },
    // computedWindowSize() {
    //   if (this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
    //     return {position: 'absolute', top: 0, width: '95%', maxWidth: '380px', marginTop: '10px'}
    //   } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
    //     return {position: 'absolute', top: 0, width: '380px', marginTop: '25px'}
    //   } else {
    //     return {position: 'absolute', top: 0, width: '380px', marginTop: '50px'}
    //   }
    // },
    computedWindowSize() {
      if (this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '95%', maxWidth: '440px', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '440px', marginTop: '20px'}
      } else {
        return {position: 'absolute', top: 0, width: '440px', marginTop: '20px'}
      }
    },
  },
}
</script>